// Book a Teams meeting: VERA finds times that work for everyone, the person picks one, Windows asks to confirm, then it's booked.
import { useState } from "react";
import { Icon, Orb, useStudio } from "./ui";
import { cleanError, graph, openLink } from "../lib/m365";
import { L, T } from "../lib/i18n";

const pad = (n: number) => String(n).padStart(2, "0");
const localInput = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const nextHalfHour = () => { const d = new Date(); d.setMinutes(d.getMinutes() < 30 ? 30 : 60, 0, 0); d.setHours(d.getHours() + (d.getMinutes() === 0 ? 0 : 0)); return d; };

export function MeetingDialog({ onClose, initial }: { onClose: () => void; initial?: { subject?: string; attendees?: string; agenda?: string } }) {
  const { toast } = useStudio();
  const [subject, setSubject] = useState(initial?.subject ?? "");
  const [who, setWho] = useState(initial?.attendees ?? "");
  const [start, setStart] = useState(localInput(nextHalfHour()));
  const [mins, setMins] = useState(30);
  const [agenda, setAgenda] = useState(initial?.agenda ?? "");
  const [slots, setSlots] = useState<{ start: string; end: string }[] | null>(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const list = () => who.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);

  async function find() {
    setBusy("find"); setError(""); setSlots(null);
    try {
      const r = await graph<{ slots: { start: string; end: string }[]; reason: string }>("meeting.findTimes", { attendees: list(), minutes: mins });
      setSlots(r.slots);
      if (!r.slots.length) setError(r.reason === "AttendeesUnavailable" ? L("No time works for everyone in the next week.", "Asnjë orar nuk u përshtatet të gjithëve javën e ardhshme.") : L("No suggestions came back. Pick a time yourself.", "Nuk erdhën sugjerime. Zgjidh vetë një orar."));
    } catch (e) { setError(cleanError(e)); } finally { setBusy(""); }
  }
  async function book() {
    setBusy("book"); setError("");
    try {
      const r = await graph<{ cancelled?: boolean; webLink?: string }>("meeting.create", { subject, start, minutes: mins, attendees: list(), agenda });
      if (r.cancelled) { setBusy(""); return; }
      toast(L("Booked. The invite is in everyone's calendar.", "U caktua. Ftesa është në kalendarin e të gjithëve."));
      openLink(r.webLink);
      onClose();
    } catch (e) { setError(cleanError(e)); setBusy(""); }
  }

  return (
    <div className="quick-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card quick" role="dialog" aria-modal="true" aria-label={L("New meeting", "Takim i ri")} style={{ maxWidth: 560 }}>
        <div className="row" style={{ gap: 10 }}><Orb size={26} mode="breathe" /><h2 style={{ fontSize: 19 }}>{L("New meeting", "Takim i ri")}</h2></div>
        <label className="fld"><span>{L("Title", "Titulli")}</span><input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={L("Weekly planning", "Planifikimi javor")} /></label>
        <label className="fld"><span>{L("People (email addresses)", "Njerëzit (adresat e emailit)")}</span><input value={who} onChange={(e) => setWho(e.target.value)} placeholder={T("name@company.com")} /></label>
        <div className="row" style={{ gap: 10, flexWrap: "nowrap" }}>
          <label className="fld" style={{ flex: 1 }}><span>{L("Starts", "Fillon")}</span><input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} /></label>
          <label className="fld" style={{ width: 130 }}><span>{L("Length", "Kohëzgjatja")}</span><select value={mins} onChange={(e) => setMins(Number(e.target.value))}>{[15, 30, 45, 60, 90].map((m) => <option key={m} value={m}>{m} min</option>)}</select></label>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn" disabled={!list().length || !!busy} onClick={() => void find()}><Icon name="sparkle" size={14} /> {busy === "find" ? L("Checking calendars…", "Po kontrollohen kalendarët…") : L("Find a time that works", "Gjej një orar që u përshtatet")}</button>
        </div>
        {slots && !!slots.length && <div className="row" style={{ gap: 6 }}>{slots.map((s) => (
          <button key={s.start} className={`chip ${start === s.start.slice(0, 16) ? "on" : ""}`} onClick={() => setStart(s.start.slice(0, 16))}>
            {new Date(s.start).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" })}
          </button>))}</div>}
        <label className="fld"><span>{L("Agenda (optional)", "Rendi i ditës (opsional)")}</span><textarea value={agenda} onChange={(e) => setAgenda(e.target.value)} rows={3} /></label>
        {error && <p style={{ color: "var(--bad)", fontSize: 13 }} role="alert">{error}</p>}
        <div className="row" style={{ justifyContent: "space-between" }}>
          <span className="faint" style={{ fontSize: 12 }}>{L("You confirm before anything is booked.", "Ti e konfirmon para se të caktohet diçka.")}</span>
          <div className="row" style={{ gap: 8 }}><button className="btn" onClick={onClose}>{L("Cancel", "Anulo")}</button>
            <button className="btn primary" disabled={!subject.trim() || !!busy} onClick={() => void book()}>{busy === "book" ? L("Booking…", "Po caktohet…") : L("Book meeting", "Cakto takimin")}</button></div>
        </div>
      </div>
    </div>
  );
}
